import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import DtvText, { IDtvTextProps } from "../dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import CardMedia from "@mui/material/CardMedia";
import { resolveChannelLogoSrc, channelLogoSx, channelLogoChipFor, handleChannelLogoError } from "lib/helper/channel-logo-url";
import ChannelLogoChip from "@/shared/ChannelLogoChip";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { fusionScrollBarStyles } from "@directv-digital/dds.core.commons";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import Tooltip, { FusionTooltipProps } from "@directv-digital/fusion.molecules.tooltip";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import ZipcodeModule, { ZipcodeModuleProps } from "src/components/video-lab/zipcode-module";
import { handleLocalRsnChannelFilter, handleNationalChannelDMAFilter } from "lib/service/channels/get-local-national-dma-eligible-channels";
import ChannelInfoIcon from "@/shared/ChannelInfoIcon";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

export type ChannelPackLineupProps = {
  id: string;
  heading: string;
  channelsTitle: string;
  subscriptionsTitle: string;
  packages: ChannelPackLineupPackageProps[];
  localChannelLegal: string;
  channelListingData: any;
  queryParams: any;
  allChannels: any;
  queryParameters?: any;
  genreLocalChannelMapping?: any;
  zipCodeModule?: {
    ctaLabel: string;
    zipcodeLabel: string;
    errorZipCodeMsg: string;
    clearZipCodeAccesibilityText: string;
    searchZipCodeAccesibilityText: string;
  };
  rsnTeamsTableData?: {
    title: string;
    channelsTitle: string;
    teamsTitle: string;
    channelNumberText: string;
    teams: string[];
    errorMessage: string;
    legal: string;
  };
  spinner?: {
    content: string;
  };
  myHomeTeamNationalChannels?: ChannelPackLineupChannelProps[];
  genreNationals?: any;
  zipcodeModuleConfig?: ZipcodeModuleProps["ciConfig"];
  localsSectionLocationTop?: boolean;
  localsSectionTooltip?: FusionTooltipProps;
  noLocalsInAreaText?: IDtvTextProps;
  noLocalsInPackText?: IDtvTextProps;
  nationalsDisclaimerText?: IDtvTextProps;
  showLocalChannelsPerGenre?: string;
  localChannelsInPackage?: any;
  zipcode?: string;
};

export type ChannelPackLineupPackageProps = {
  packageKey: string;
  packageName: string;
  packageIcon: string;
  packagePrice: number;
  subscriptions?: ChannelPackLineupSubscriptionProps[];
  channels: ChannelPackLineupChannelProps[];
  localChannelCount: number;
  localChannelLegal: string;
  channelCount: string;
  channelsTitle: string;
  tooltip?: FusionTooltipProps;
  contentFriendlyPackageKey?: string;
};

export type ChannelPackLineupSubscriptionProps = {
  name: string;
  logo: string;
  subHeading: string;
  expandCta: string;
  expandContent: any;
};

export type ChannelPackLineupChannelProps = {
  channelNameLabel: string;
  channelLogoPath: string;
  isLocal: boolean;
  isRsn: boolean;
  isNational?: boolean;
  // WAT-11362: the server's per-channel OV-logo decision, carried on the data (never a
  // client flag lookup). Optional because the CMS-fed path does not set it.
  isOvLogo?: boolean;
};

const styles = {
  headingContainer: {
    px: {
      xs: "20px",
      sm: "32px",
      md: "48px"
    },
    pb: {
      xs: "16px",
      sm: "24px",
      md: "24px"
    }
  },
  zipcodeContainer: {
    pt: {
      xs: '16px',
      sm: "24px ",
      md: "24px"
    }
  },
  heading: {
    color: "#102641"
    // fontSize: { xs: "24px", sm: "30px", md: "36px" },
  },
  packageHeaderContainer: {
    display: "flex",
    height: {
      xs: "40px",
      sm: "48px",
      md: "48px"
    },
    justifyContent: "space-between",
    alignItems: "center",
    alignSelf: "stretch",
    background: "#102641"
  },
  subscriptionHeadingContainer: {
    p: {
      xs: "32px 20px 0px 20px",
      sm: "24px 20px 0px 32px",
      md: "32px 20px 0px 48px"
    }
  },
  subscriptionsContainer: {
    display: "flex",
    p: {
      xs: "24px 20px 0px 20px",
      sm: "24px 140px 0px 140px",
      md: "24px 140px 0px 140px"
    },
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: {
      xs: "24px",
      sm: "12px",
      md: "12px"
    },
    alignSelf: "stretch"
  },
  subscriptionContainer: {
    display: "flex",
    p: {
      xs: "12px 16px 12px 16px",
      sm: "16px 16px 16px 16px",
      md: "16px 16px 16px 16px"
    },
    alignItems: "start",
    flexDirection: "column",
    gap: "16px",
    alignSelf: "stretch",
    borderRadius: "3px",
    background: "#FFFFFF",
    boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.16)"
  },
  subscriptionTopContainer: {
    display: "flex",
    alignItems: "center",
    gap: "24px"
  },
  subscriptionLogo: {
    width: "64px",
    height: "64px",
    flexShrink: 0,
    objectFit: "contain",
    borderRadius: "10px",
    background: "50% / cover no-repeat"
  },
  subscriptionTopBodyContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    flexShrink: 0
  },
  subscriptionBottomContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    alignSelf: "stretch"
  },
  subscriptionBottomTextContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    alignSelf: "stretch"
  },
  subscriptionBottomImageContainer: {
    display: "flex",
    height: "144px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    overflowX: "auto",
    overflowY: "hidden",
    ...fusionScrollBarStyles()
  },
  subscriptionBottomImageStyles: {
    width: {
      xs: "300px",
      sm: "425px",
      md: "407.602px"
    },
    height: {
      xs: "100px",
      sm: "144px",
      md: "144px"
    },
    flexShrink: 0,
    borderRadius: "5px",
    background: "lightgray 0px -0.873px / 100% 170.247% no-repeat"
  },
  channelGridContainer: {
    display: "flex",
    padding: {
      xs: "8px 0px 20px 0px",
      sm: "20px 36px 20px 36px",
      md: "20px 40px 20px 40px"
    },
    justifyContent: {
      xs: "center",
      sm: "start"
    },
    alignItems: "flex-start",
    alignContent: "flex-start",
    gap: {
      xs: "16px 12px",
      sm: "16px 16px"
    },
    alignSelf: "stretch",
    flexWrap: "wrap"
  },
  channelLogoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px",
    position: "relative"
    // width: {
    //   xs: "82px !important",
    //   sm: "88px !important",
    // },
    // p: "12px 9px 12px 9px",
    // background: "#FBFBFB",
  },
  channelLogo: {
    display: "flex",
    width: "80px",
    height: "80px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "300px",
    // WAT-11362: contain (overrides the MUI CardMedia default `cover`) so the
    // 120×91 landscape OV logo shows in FULL instead of being side-cropped in
    // the circle. No-op for the square logoUrl icons — flag-off unchanged.
    objectFit: "contain"
    // WAT-11362: the circular badge + centring geometry live on the logo img itself
    // (channelLogoSx), applied at the render site.
  },
  localChannelLegalContainer: {
    textAlign: "center",
    p: "16px 20px 24px 20px"
  },
  localChannelLegal: {
    color: "#102641"
  },
  closeButtonContainer: {
    "& a": {
      width: {
        xs: "100%",
        sm: "auto"
      }
    },
    m: {
      xs: "20px",
      sm: "32px",
      md: "32px"
    },
    mx: { md: "32px" }
  },
  tooltipArrow: {
    position: "absolute",
    top: "0px",
    transform: "translate3d(0px, 8.8px, 0px)",
    left: 0,
    marginLeft: "-0.71em",
    height: "1em",
    width: "0.71em",
    overflow: "hidden",
    boxSizing: "border-box",
    color: "rgba(97, 97, 97, 0.9)",
    "::before": {
      transformOrigin: "100% 100%"
    }
  },
  tooltipStyles: {
    maxWidth: "230px",
    opacity: "1",
    transform: "none",
    transition: "opacity 200ms cubicBezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubicBezier(0.4, 0, 0.2, 1) 0ms",
    transformOrigin: "left center",
    backgroundColor: "rgba(97, 97, 97, 0.92)",
    borderRadius: "4px",
    color: "#fff",
    padding: "4px 8px",
    fontSize: "0.6875rem",
    width: "fit-content",
    margin: "0",
    wordWrap: "break-word",
    fontWeight: "500",
    position: "relative",
    marginLeft: "0px!important"
  },
  rsnTableContainer: {
    display: "flex",
    p: {
      xs: "8px 20px 0px 20px",
      sm: "20px 20px 0px 32px",
      md: "20px 20px 0px 48px"
    },
    justifyContent: {
      xs: "center",
      sm: "start"
    },
    flexDirection: "column",
    alignItems: "flex-start",
    alignContent: "flex-start",
    alignSelf: "stretch",
    flexWrap: "wrap"
  },
  rsnTableHeader: {
    display: "flex",
    padding: {
      xs: "12px 30px",
      sm: "12px 0px",
      md: "12px 0px"
    },
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    border: "1px solid #D1D1D1"
  },
  rsnTableBody: {
    display: "flex",
    padding: {
      xs: "2px 3px 7px 3px",
      sm: "24px 20px",
      md: "31px 20px"
    },
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    borderRight: "1px solid #D1D1D1",
    borderBottom: "1px solid #D1D1D1",
    borderLeft: "1px solid #D1D1D1"
  },
  strikeThrough: {
    position: "absolute",
    overflow: "hidden",
    background: "linear-gradient(to left top,#0000 47%,#FF0000,#0000 53%)",
    borderRadius: "6px",
    padding: "2px 4px",
    color: "#FFFFFF",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%"
  },
  localChannelText: {
    fontFamily: "roboto-regular",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "130%",
    color: "#FFFFFF"
  }
};

const AccordionExpandIcon = ({ iconUrl }): any => (
  <DtvButton
    ariaLabel={"expand-collapse-icon"}
    imageUrl={iconUrl}
    mode="dark"
    tabIndex={-1}
    variant="uxicon"
    sx={{
      width: "28px",
      height: "28px",
      flexShrink: 0,
      paddingLeft: "0px !important",
      paddingRight: "0px !important",
      cursor: "default",
      "& div": {
        width: "28px",
        height: "28px"
      }
    }}
  />
);

const DownArrowIcon = () => (
  <svg fill="none" height="21" viewBox="0 0 20 21" width="20" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.78877 7.98627C2.78877 7.64035 2.91481 7.28875 3.17237 7.02222C3.68748 6.49483 4.52044 6.49483 5.03007 7.02222L9.72089 11.8878L14.4227 7.01088C14.9378 6.47782 15.7653 6.47782 16.2804 7.01088C16.7955 7.54394 16.7955 8.40024 16.2804 8.9333L11.0196 14.3887C10.6799 14.7403 10.2086 14.9444 9.71541 14.9444C9.22769 14.9444 8.75094 14.7459 8.41118 14.3944L3.16143 8.95032C2.90388 8.68378 2.77783 8.33786 2.77783 7.99194"
      fill="#2E70BD"
    />
  </svg>
);

const UpArrowIcon = () => (
  <svg fill="none" height="21" viewBox="0 0 20 21" width="20" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.6557 13.0137C16.6557 13.3596 16.5242 13.7112 16.2721 13.9777C15.757 14.5051 14.9241 14.5051 14.4145 13.9777L9.72363 9.11211L5.02187 13.9891C4.50676 14.5221 3.67928 14.5221 3.16417 13.9891C2.64905 13.456 2.64905 12.5997 3.16417 12.0666L8.42489 6.61128C8.76464 6.25968 9.23592 6.05554 9.72911 6.05554C10.2168 6.05554 10.6936 6.25401 11.0333 6.6056L16.2831 12.0496C16.5407 12.3161 16.6667 12.6621 16.6667 13.008"
      fill="#2E70BD"
    />
  </svg>
);

// Single source for the sticky-summary offset custom property — producer (parent body
// container) and consumer (panel summary top) must never drift apart on the name.
const SUMMARY_TOP_VAR = "--cpl-summary-top";

type ChannelChipProps = {
  channel: ChannelPackLineupChannelProps;
  chnlindex: number;
  genreNationalData: any;
  index: number;
  loading: boolean;
};

// One channel logo + its tooltips. Extracted so the tooltip-placement state is chip-local:
// a touch/hover recomputes placement by re-rendering this chip only, not the whole lineup.
const ChannelChip = ({ channel, chnlindex, genreNationalData, index, loading }: ChannelChipProps) => {
  const [placement, setPlacement] = useState<"top" | "bottom" | undefined>(undefined);
  const handleTooltipPlacement = () => {
    if (channel?.isLocal || channel?.isRsn || channel?.isNational) {
      const iconEl = document?.getElementById?.(`icon-container-${index}-${chnlindex}`);
      const headerEl = document?.getElementById?.(`panel1a-header-${index}`);
      if (iconEl && headerEl) {
        const tooltipBox = iconEl?.getBoundingClientRect();
        const headerBox = headerEl?.getBoundingClientRect();
        const isOverlappingHeader = tooltipBox?.top < headerBox?.bottom;
        setPlacement(isOverlappingHeader ? "bottom" : "top");
      }
    }
  };
  try {
    return (
      <DtvBox
        aria-label={`${channel?.channelNameLabel} icon`}
        className={`channel-tooltip-${chnlindex}`}
        id={`icon-container-${index}-${chnlindex}`}
        tabIndex={-1}
        sx={{
          ...styles.channelLogoContainer
        }}
        onMouseEnter={
          _event => ((channel?.isLocal || channel?.isRsn || channel?.isNational) ?
              handleTooltipPlacement() :
              undefined)
        }
        onTouchStart={
          _event => ((channel?.isLocal || channel?.isRsn || channel?.isNational) ?
              handleTooltipPlacement() :
              undefined)
        }
      >
        <Tooltip
          describeChild
          closeOnScroll={true}
          content={channel?.channelNameLabel}
          disableCursorPointer={true}
          enterTouchDelay={0}
          key={chnlindex}
          tabIndex={-1}
          tooltipStyles={styles.tooltipStyles}
          PopperProps={{
            disablePortal: false,
            modifiers: [
              {
                name: "preventOverflow",
                enabled: true
              },
              {
                name: "flip",
                enabled: true
              },
              {
                name: "offset",
                options: {
                  offset: [0, -20]
                }
              }
            ],
            sx: {
              width: "unset !important",
              zIndex: 2,
              "& .MuiTypography-root": {
                fontFamily: `roboto-regular`,
                fontSize: "14px"
              }
           }
          }}
          placement={
            (channel?.isLocal || channel?.isRsn || channel?.isNational) ?
              (placement ?? "top") :
              "bottom"
          }
          sx={{
            "& #popper[data-popper-escaped]": {
              visibility: "hidden"
            }
          }}
        >
          {loading ?
          <DtvBox sx={{
 ...styles.channelLogo,
paddingTop: "1.3px"
}}>
            <svg viewBox="0 0 261 261" xmlns="http://www.w3.org/2000/svg">
              <g><circle cx="108" cy="108" data-name="Ellipse 34" fill="#E4E4E4" r="108" transform="translate(22.5 20.5)"/></g>
            </svg>
          </DtvBox>                                    :
          <ChannelLogoChip
            key={index}
            alt={channel?.channelNameLabel || "channel logo icon"}
            imgSx={styles.channelLogo}
            size="80px"
            isOvLogo={channel?.isOvLogo}
            src={channel?.channelLogoPath}
            testId="card-media"
          />}
        </Tooltip>
        {(channel?.isLocal || channel?.isRsn || (channel?.isNational && genreNationalData?.nationaltooltipContent)) && (
          <Tooltip
            closeOnScroll={true}
            enterTouchDelay={0}
            placement="top"
            PopperProps={{
              disablePortal: true,
              sx: {
                "& .MuiTooltip-tooltip": {
                  backgroundColor: `${colorPalette.neutral_white.main} !important`,
                  color: "#102641 !important",
                  fontWeight: "400 !important",
                  fontSize: "14px !important",
                  position: "relative",
                  boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.15)",
                  padding: "12px"
                },
                "& .MuiTypography-root": {
                  fontFamily: `roboto-regular`,
                  fontSize: "14px"
                }
              }
            }}
            content={
               channel?.isNational ?
               genreNationalData?.nationaltooltipContent || "" :
              channel?.isLocal ?
                "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them." :
                "Regional Sports Networks aren't available everywhere and may vary by package and location. You may need to be in your billing region (ZIP Code) to watch them."
            }
          >
            <DtvBox
              component="div"
              sx={{
                cursor: "pointer",
                width: "100%",
                margin: "0 auto"
              }}
            >
              <DtvBox sx={{
                  position: "absolute",
                  bottom: "-6px",
                  left: "16px",
                  maxWidth: "80px",
                  maxHeight: "80px",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  backgroundColor: colorPalette.gray[300],
                  borderRadius: "4px",
                  m: "-4px auto 0px auto",
                  p: "5px"
                }}>
                <DtvText
                  content={channel?.isNational ? genreNationalData?.nationalTooltipLabel : channel?.isLocal ? "Local" : "RSN"}
                  variant="smalltext"
                  sx={{
                    color: "#FFFFFF",
                    textAlign: "center",
                    fontSize: "10px !important"
                  }}
                />
                <ChannelInfoIcon color={colorPalette.white.main} />
              </DtvBox>
            </DtvBox>
          </Tooltip>
        )}
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="DTV ChannelPackLineup component" data-testid="error-display" error={error} />;
  }
};
export const LocalChannelsSection = ({ packageSpecificLocalChannels, pack, localChannelsInPackage, noLocalsInAreaText, noLocalsInPackText, nationalDisclaimerFlag, localChannels, localsSectionTooltip, nationalsDisclaimerText }) => (
    <DtvBox sx={{
 paddingX: {
 xs: "20px",
sm: "32px",
md: "48px"
},
marginTop: "8px"
}}>
      {Object.keys(packageSpecificLocalChannels?.[pack?.packageKey]).length > 0 ?
      <DtvText content={`(${Object.keys(packageSpecificLocalChannels?.[pack?.packageKey]).length}) local channel(s) included in your ZIP Code`} variant="body3" />      :
      localChannelsInPackage?.[pack?.packageKey] ?
      <DtvText {...noLocalsInAreaText?.[0]} />      :
      <DtvText {...noLocalsInPackText?.[0]} />
      }
      {/* Available local channels (channels without strike through) */}
      <DtvBox sx={{
 display: "flex",
gap: "8px",
flexWrap: "wrap",
marginTop: "4px",
marginBottom: "8px"
}}>
        {Object.keys(packageSpecificLocalChannels?.[pack?.packageKey]).map((localChannelKey, index) => {
          const channel = packageSpecificLocalChannels?.[pack?.packageKey]?.[localChannelKey];
          return (
            <DtvBox key={index} sx={{
 background: "#555555",
borderRadius: "6px",
padding: "2px 4px"
}}>
              <DtvText content={`${channel?.channelNameLabel}${nationalDisclaimerFlag ? "*" : ""}`} sx={styles.localChannelText} />
            </DtvBox>
          )
        })}
        {/* Unavailable local channels (channels with strike through) only shown if there are available channels in the packageSpecificLocalChannels */}
        {Object.keys(packageSpecificLocalChannels?.[pack?.packageKey]).length > 0 && localChannels && typeof localChannels === "string" && localChannels !== "" && localChannels?.split(",").map((localChannel, index) => {
          if(!packageSpecificLocalChannels?.[pack?.packageKey]?.[localChannel]) {
            return (
              <DtvBox key={index} sx={{
 position: "relative",
backgroundColor: "#8D8D8D",
borderRadius: "6px",
padding: "2px 4px"
}}>
                <DtvBox sx={styles.strikeThrough} />
                <DtvText content={localChannel} sx={styles.localChannelText} />
              </DtvBox>
            )
          }
          return null;
        })}
        {localsSectionTooltip && localsSectionTooltip?.[0]?.content !== "" &&
          <Tooltip
            {...localsSectionTooltip?.[0]}
            closeOnScroll={true}
            PopperProps={{
              sx: {
                "& .MuiTypography-root": {
                  fontFamily: `roboto-regular`,
                  fontSize: "14px"
                }
              }
            }} />}
      </DtvBox>
      {nationalDisclaimerFlag && <DtvText sx={{
 fontFamily: "roboto-regular",
fontWeight: 400,
lineHeight: "130%",
fontSize: "12px !important",
marginBottom: "8px"
}} {...nationalsDisclaimerText?.[0]} />}
    </DtvBox>
)

type ChannelPackPanelProps = Pick<ChannelPackLineupProps,
  "localChannelsInPackage" | "localsSectionLocationTop" | "localsSectionTooltip" |
  "nationalsDisclaimerText" | "noLocalsInAreaText" | "noLocalsInPackText" |
  "rsnTeamsTableData" | "showLocalChannelsPerGenre" | "subscriptionsTitle"
> & {
  expanded: boolean;
  genreNationalMapping: any;
  index: number;
  loading: boolean;
  myHomeTeamRsnChannels: any[];
  nationalDisclaimerFlag: boolean;
  onExpandCtaClick: (event: React.MouseEvent | React.KeyboardEvent, name: string | null, expandCta: string) => void;
  onToggle: (index: number) => void;
  // Name of the subscription currently expanded in THIS pack, else null. Derived per-panel
  // in the parent so a subscription tap only changes the prop of the panels involved.
  openSubscriptionName: string | null;
  pack: ChannelPackLineupPackageProps;
  packageSpecificLocalChannels: Record<string, any>;
};

// One package accordion. Memoized so an accordion toggle re-renders the toggled panel
// only — the top-level accordionExpanded state used to re-render every panel per toggle.
const ChannelPackPanel = React.memo(({
  expanded,
  index,
  loading,
  localChannelsInPackage,
  localsSectionLocationTop,
  localsSectionTooltip,
  genreNationalMapping,
  myHomeTeamRsnChannels,
  nationalDisclaimerFlag,
  nationalsDisclaimerText,
  noLocalsInAreaText,
  noLocalsInPackText,
  onExpandCtaClick,
  onToggle,
  openSubscriptionName,
  pack,
  packageSpecificLocalChannels,
  rsnTeamsTableData,
  showLocalChannelsPerGenre,
  subscriptionsTitle
}: ChannelPackPanelProps) => {
  try {
    return (
      <Accordion
        TransitionProps={{ unmountOnExit: true }}
        data-testid="accordion"
        expanded={expanded}
        sx={{
          mb: "4px !important",
          "&.Mui-expanded": {
            margin: "0px !important",
            boxShadow: "none"
          }
          // position: "relative",
        }}
        onChange={() => onToggle(index)}
      >
        <AccordionSummary
          aria-controls="panel1a-content"
          data-testid="accordion-summary"
          id={`panel1a-header-${index}`}
          expandIcon={
            // Parity: the pre-extraction code keyed this on an always-truthy state object,
            // so the down arrow always won; MUI rotates expandIcon for the expanded state.
            <AccordionExpandIcon iconUrl="https://www.directv.com/dtvassets/global/icons/2022/arrow-down_dk-bg.svg" />
          }
          sx={{
            background: pack?.packageKey === "MYFREE_DTV" ? "#000599" : "#102641",
            color: "#FFF",
            position: "sticky",
            // Set as a CSS custom property on the panels' container so the ResizeObserver
            // header-height updates don't invalidate every memoized panel via a prop.
            top: `var(${SUMMARY_TOP_VAR}, 0px)`,
            zIndex: 3,
            px: {
 xs: "20px",
sm: "32px",
md: "48px"
},
            py: pack?.packageKey === "MYFREE_DTV" && "6px",
            minHeight: {
              xs: "40px !important",
              sm: "48px !important",
              md: "48px !important"
            },
            "& > div:first-of-type": {
              m: "0px !important"
            }
          }}
        >
          {/* package header */}
          <DtvGrid sx={{
 ...styles.packageHeaderContainer,
background: pack?.packageKey === "MYFREE_DTV" ? "#000599" : "#102641"
}}>
            {/* genre pack name & icon */}
            <DtvBox
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center"
              }}
            >
              {pack?.packageKey !== "MYFREE_DTV" && pack?.packageIcon && <DtvButton
                ariaLabel={`${pack?.packageName  } ` + `icon`}
                imageUrl={pack?.packageIcon}
                mode="dark"
                tabIndex={-1}
                variant="uxicon"
                sx={{
                  width: "28px",
                  height: "28px",
                  flexShrink: 0,
                  paddingLeft: "0px !important",
                  paddingRight: "0px !important",
                  m: "0 4px 0 0",
                  cursor: "default",
                  "& div": {
                    width: "28px",
                    height: "28px",
                    borderRadius: "unset !important"
                  }
                }}
              />}
              {pack?.packageKey === "MYFREE_DTV" && (
                <DtvBox
                  aria-label={"My Free Directv"}
                  id="icon-container"
                  tabIndex={-1}
                  sx={{
 width: "auto",
height: "30px",
"& img": {
 width: "100%",
height: "100%",
objectFit: "contain"
},
mr: "12px"
}}
                >
                  {/* logo */}
                  <CardMedia
                    alt={`${pack?.packageName} logo`}
                    aria-labelledby={`icon-container-${pack?.packageName}`}
                    component={"img"}
                    data-testid="card-media"
                    loading="lazy"
                    src={pack?.packageIcon}
                  />
                </DtvBox>
              )}
              <DtvText
                content={`${pack?.packageName || ""} ${pack?.channelCount?.trim?.() ? `(${pack?.channelCount})` : ""}`}
                variant={pack?.packageKey === "MYFREE_DTV" ? "body3" : "body2medium"}
                sx={{
                 color: "#FFF",
                 "& span": {
                  marginLeft: "12px"
                  },
                 "& svg path": {
                  fill: `${colorPalette.neutral_white.main} !important`
                 }
                }}
                tooltip={pack?.tooltip?.[0]?.content ? {
 ...pack?.tooltip?.[0],
mode: 'dark',
stopPropagation: true,
closeOnScroll: true
} : undefined}
              ></DtvText>
            </DtvBox>
            {/* genre pack price */}
            <DtvBox>
              <DtvText content={pack.packagePrice && `$${pack.packagePrice}/mo.`} sx={{ color: "#FFF" }} variant={"body2medium"} />
            </DtvBox>
          </DtvGrid>
        </AccordionSummary>
        <AccordionDetails
          data-testid="accordion-details"
          sx={
            {
              // border: "none",
              zIndex: 1
            }
          }
        >
          {/* subscriptions section */}
          {pack?.subscriptions?.[0]?.name && (
            <>
              {/* subscriptions heading */}
              <DtvGrid key={index} sx={{ ...styles.subscriptionHeadingContainer }}>
                <DtvText content={subscriptionsTitle} sx={{ color: "#102641" }} variant="body1medium" />
              </DtvGrid>
              {/* subscriptions body */}
              <DtvGrid
                sx={{
                  ...styles.subscriptionsContainer
                }}
              >
                {pack?.subscriptions?.map((subscription, index) => {
                  // fail-closed for the whole nullish class: a subscription with a null OR
                  // missing CMS name can never read as open (the closed sentinel is null)
                  const isSubOpen = subscription?.name != null && openSubscriptionName === subscription.name;
                  return (
                    <DtvGrid
                      key={index}
                      sx={{
                        ...styles.subscriptionContainer
                      }}
                    >
                      {/* subscriptions top container */}
                      <DtvGrid sx={{ ...styles.subscriptionTopContainer }}>
                        {/* subscription logo */}
                        <CardMedia
                          alt={subscription?.name || "channel logo icon"}
                          aria-labelledby={`icon-container-${subscription?.name}`}
                          component={"img"}
                          data-testid="card-media"
                          key={index}
                          loading="lazy"
                          src={`https://www.directv.com/${subscription.logo}`}
                          sx={{
                            ...styles.subscriptionLogo
                          }}
                        />
                        <DtvBox
                          sx={{
                            ...styles.subscriptionTopBodyContainer
                          }}
                        >
                          {/* subscription name */}
                          <DtvGrid
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              alignSelf: "stretch"
                            }}
                          >
                            <DtvText content={subscription?.name} variant="subhead2medium" sx={{
 color: "#102641",
width: { xs: "200px" }
}} />
                          </DtvGrid>
                          {/* subscription sub heading */}
                          <DtvGrid
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              alignSelf: "stretch"
                            }}
                          >
                            <DtvText content={subscription?.subHeading} sx={{ color: "#102641" }} variant="body1" />
                          </DtvGrid>
                          {/* subscription expand cta */}
                          <DtvGrid
                            component="a"
                            tabIndex={0}
                            {...getAnalyticsAttrs(
                              isSubOpen ? "close" : subscription?.expandCta?.toLowerCase(),
                              'modal_channel pack lineup',
                              typeof window !== 'undefined' ? window?.location?.href : ''
                            )}
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "flex-end",
                              alignSelf: "stretch",
                              width: "fit-content",
                              "& svg": {
                                position: "relative"
                                // top: "0.5px",
                              }
                            }}
                            onClick={e => {
                              onExpandCtaClick(e, subscription?.name, subscription?.expandCta);
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                onExpandCtaClick(e, subscription?.name, subscription?.expandCta);
                              }
                            }}
                          >
                            <DtvText
                              content={isSubOpen ? "Close" : subscription?.expandCta}
                              variant="body2medium"
                              sx={{
 color: "#2E70BD",
mr: "4px"
}}
                            />
                            {isSubOpen ? <UpArrowIcon /> : <DownArrowIcon />}
                          </DtvGrid>
                        </DtvBox>
                      </DtvGrid>
                      {/* subscriptions bottom container */}
                      <DtvGrid
                        sx={{
                          ...styles.subscriptionBottomContainer,
                          display: isSubOpen ? "flex" : "none"
                        }}
                      >
                        {/* text container */}
                        <DtvGrid
                          sx={{
                            ...styles.subscriptionBottomTextContainer
                          }}
                        >
                          {/* description */}
                          {subscription?.expandContent?.description && (
                            <DtvText
                              content={subscription?.expandContent?.description}
                              variant="body1"
                              sx={{
                                color: "#102641",
                                alignSelf: "stretch"
                              }}
                            />
                          )}
                          {/* bullet points */}
                          <DtvGrid
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              flexFlow: "column nowrap"
                            }}
                          >
                            {subscription?.expandContent?.bulletList?.map((bulletPoint, index) => (
                                <DtvGrid
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    lineHeight: "200%",
                                    mr: "24px"
                                  }}
                                >
                                  <DtvText
                                    content="•"
                                    variant="body1"
                                    sx={{
                                      color: "#102641",
                                      alignSelf: "stretch",
                                      mr: "10px",
                                      position: "relative",
                                      top: "8px"
                                    }}
                                  />
                                  <DtvText
                                    content={bulletPoint}
                                    variant="body1"
                                    sx={{
                                      color: "#102641",
                                      alignSelf: "stretch",
                                      p: "8px 0px"
                                    }}
                                  />
                                </DtvGrid>
                              ))}
                          </DtvGrid>
                        </DtvGrid>
                        {/* image container */}
                        {subscription?.expandContent?.images?.length > 0 && (
                          <DtvGrid
                            sx={{
                              ...styles.subscriptionBottomImageContainer
                            }}
                          >
                            {subscription?.expandContent?.images?.map(image => (
                                <CardMedia
                                  alt={image?.alt || "channel logo icon"}
                                  aria-labelledby={`icon-container-${image?.alt}`}
                                  component={"img"}
                                  data-testid="card-media"
                                  key={index}
                                  loading="lazy"
                                  src={`https://www.directv.com/${image?.url}`}
                                  sx={{
                                    ...styles.subscriptionBottomImageStyles
                                  }}
                                />
                              ))}
                          </DtvGrid>
                        )}
                      </DtvGrid>
                    </DtvGrid>
                  );
                })}
              </DtvGrid>
            </>
          )}
          {/* MyHomeTeam Teams Section */}
          {(pack?.packageKey === "ATT_TV_MYHOMETEAM") && (
            <>
              {/* RSN title */}
              <DtvGrid
                sx={{
                  p: {
                    xs: "20px 20px 0px 20px",
                    sm: "24px 20px 0px 32px",
                    md: "32px 20px 0px 48px"
                  }
                }}
              >
                <DtvText content={rsnTeamsTableData?.[0]?.title || "Regional Sports Networks"} sx={{ color: "#102641" }} variant="body1medium" />
              </DtvGrid>
              {/* RSN teams table */}
              {myHomeTeamRsnChannels?.length > 0 ? (
                <>
                  <DtvGrid sx={{ ...styles.rsnTableContainer }}>
                    {/* Table Header */}
                    <DtvBox sx={{
 display: "flex",
alignItems: "center",
alignSelf: "stretch",
width: {
 xs: "100%",
sm: "536px",
md: "806px"
}
}}>
                      <DtvBox
                        sx={{
                          ...styles.rsnTableHeader,
                          background: "#102641",
                          width: "50%"
                        }}
                      >
                        <DtvText
                          content={rsnTeamsTableData?.[0]?.channelsTitle || "Channels"}
                          variant="subhead2medium"
                          sx={{
 color: "#FFF",
textAlign: "center",
flexShrink: 0
}}
                        />
                      </DtvBox>
                      <DtvBox
                        sx={{
                          ...styles.rsnTableHeader,
                          background: "#EAF1FA",
                          width: "50%"
                        }}
                      >
                        <DtvText
                          content={rsnTeamsTableData?.[0]?.teamsTitle || "Team(s)"}
                          variant="subhead2medium"
                          sx={{
 color: "#000",
textAlign: "center",
flexShrink: 0
}}
                        />
                      </DtvBox>
                    </DtvBox>
                    {/* Table Body */}
                    {myHomeTeamRsnChannels?.map((channel, index) => (
                      <DtvBox
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          alignSelf: "stretch",
                          height: "100%",
                          width: {
 xs: "100%",
sm: "536px",
md: "806px"
},
                          position: "relative"
                        }}
                      >
                        {/* Channels */}
                        <DtvBox
                          sx={{
                            ...styles.rsnTableBody,
                            gap: "10px",
                            width: "50%"
                          }}
                        >
                          {/* cannel logo */}
                          <DtvBox
                            // eslint-disable-next-line no-constant-binary-expression
                            aria-label={`${channel?.channelNameLabel} icon` || "click a channel icon to view available packages"}
                            id={`icon-container-${channel?.channelNameLabel}`}
                            key={index}
                            tabIndex={-1}
                            sx={{
                              ...styles.channelLogoContainer,
                              // WAT-11362: OV artwork is a bare transparent wordmark, so the
                              // white circle the legacy SVG drew itself has to come from here.
                              // The explicit box matters: channelLogoSx sizes the img with
                              // width:100%, and on a shrink-to-fit parent that resolves to the
                              // OV file's INTRINSIC 120px (see the /120/91 in its url) — which
                              // rendered 120px OV circles next to 84px legacy badges.
                              ...(channel?.isOvLogo ? {
                                width: "80px",
                                height: "80px"
                              } : {}),
                              ...channelLogoChipFor(channel?.isOvLogo)
                            }}
                          >
                            <CardMedia
                              alt={channel?.channelNameLabel || "channel logo icon"}
                              aria-labelledby={`icon-container-${channel?.channelNameLabel}`}
                              component={"img"}
                              data-testid="card-media"
                              key={index}
                              loading="lazy"
                              src={resolveChannelLogoSrc(channel?.channelLogoPath)}
                              sx={{
                                ...styles.channelLogo,
                                ...channelLogoSx(channel?.isOvLogo)
                              }}
                              // WAT-11362: an OV imageURL can 404 — fall back to the
                              // no-logo asset rather than a broken-image icon.
                              onError={e => handleChannelLogoError(e, channel?.isOvLogo)}
                            />
                          </DtvBox>
                          {/* channel name */}
                          <DtvText content={channel?.channelNameLabel} variant="body1" sx={{
 color: "#000",
textAlign: "center"
}} />
                          {/* channel number */}
                          <DtvText
                            content={`${rsnTeamsTableData?.[0]?.channelNumberText || "Channel"} ${channel?.channelNumber}`}
                            variant="body1"
                            sx={{
 color: "#000",
textAlign: "center"
}}
                          />
                        </DtvBox>
                        {/* Team(s) */}
                        <DtvBox
                          sx={{
                            ...styles.rsnTableBody,
                            gap: "2px",
                            position: "absolute",
                            right: 0,
                            height: "100%",
                            width: "50%",
                            overflowY: "scroll",
                            ...fusionScrollBarStyles()
                          }}
                        >
                          {(rsnTeamsTableData?.[0]?.teams || ["mlb", "nba", "nhl", "wnba"])?.map(team => (
                            <DtvText content={channel?.[team]} key={index} variant="subhead2" sx={{
 color: "#000",
textAlign: "center"
}} />
                          ))}
                        </DtvBox>
                      </DtvBox>
                    ))}
                  </DtvGrid>

                </>
              ) : (
                <DtvGrid
                  sx={{
                    p: {
                      xs: "0px 20px 0px 20px",
                      sm: "0px 20px 0px 32px",
                      md: "0px 20px 0px 48px"
                    },
                    my: {
 xs: "4px",
sm: "8px"
}
                  }}
                >
                  <DtvText
                    content={rsnTeamsTableData?.[0]?.errorMessage || "There are no Regional Sports Networks in this location at this time."}
                    sx={{ color: "#102641" }}
                    variant="body3"
                  />
                </DtvGrid>
              )}
              {/* Legal */}
              <DtvGrid
                sx={{
                  p: {
                    xs: "20px 20px 0px 20px",
                    sm: "20px 20px 0px 32px",
                    md: "20px 20px 0px 48px"
                  }
                }}
              >
                <DtvText content={rsnTeamsTableData?.[0]?.legal} sx={{ color: "#102641" }} variant="smalltext" />
              </DtvGrid>
            </>
          )}
          {/* channels section */}
          {pack?.channels?.length > 0 && pack?.channels && (
            <>
              {/* channels heading */}
              <DtvGrid sx={{ ...styles.subscriptionHeadingContainer }}>
                <DtvText content={ pack?.channelsTitle} sx={{ color: "#102641" }} variant="body1medium" />
              </DtvGrid>
              {/* Local Channels Section if position is top*/}
              {Object.keys(packageSpecificLocalChannels).length > 0 && localsSectionLocationTop &&
                <LocalChannelsSection localChannels={showLocalChannelsPerGenre} localChannelsInPackage={localChannelsInPackage} localsSectionTooltip={localsSectionTooltip} nationalDisclaimerFlag={nationalDisclaimerFlag} nationalsDisclaimerText={nationalsDisclaimerText} noLocalsInAreaText={noLocalsInAreaText} noLocalsInPackText={noLocalsInPackText} pack={pack} packageSpecificLocalChannels={packageSpecificLocalChannels} />
              }
              {/* channels body */}
              <DtvGrid sx={{ ...styles.channelGridContainer }}>
                {pack?.channels?.length > 0 &&
                  pack?.channels &&
                  pack?.channels?.map((channel, chnlindex) => {
                    const genreNationalData = genreNationalMapping?.find(item => item.channel === channel?.channelNameLabel);
                    return (
                      <ChannelChip
                        channel={channel}
                        chnlindex={chnlindex}
                        genreNationalData={genreNationalData}
                        index={index}
                        key={chnlindex}
                        loading={loading}
                      />
                    );
                  })}
              </DtvGrid>
              {/* Local Channels Section if position is bottom*/}
              {Object.keys(packageSpecificLocalChannels).length > 0 && !localsSectionLocationTop &&
                <LocalChannelsSection localChannels={showLocalChannelsPerGenre} localChannelsInPackage={localChannelsInPackage} localsSectionTooltip={localsSectionTooltip} nationalDisclaimerFlag={nationalDisclaimerFlag} nationalsDisclaimerText={nationalsDisclaimerText} noLocalsInAreaText={noLocalsInAreaText} noLocalsInPackText={noLocalsInPackText} pack={pack} packageSpecificLocalChannels={packageSpecificLocalChannels} />
              }
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  } catch (error) {
    return <ErrorDisplay component="DTV ChannelPackLineup component" data-testid="error-display" error={error} />;
  }
});
ChannelPackPanel.displayName = "ChannelPackPanel";

export function ChannelPackLineup(props: ChannelPackLineupProps) {
  try {
    const [data, setData] = useState(props);
    // Immutable base copy of packages+channels (never mutate this)
    const basePackagesRef = useRef<ChannelPackLineupPackageProps[] | null>(null);
    if (!basePackagesRef.current && props?.packages) {
      basePackagesRef.current = props.packages.map(pkg => ({
        ...pkg,
        channels: [...(pkg.channels || [])]
      }));
    }
    // One object so handleExpandCtaClick can stay identity-stable; each panel receives a
    // derived openSubscriptionName instead, so a subscription tap re-renders only the
    // panel(s) whose derived value changed.
    const [openSubscription, setOpenSubscription] = useState({
      name: "",
      open: false
    });
    const [loading, setLoading] = useState(true);
    const zipCodeModalRef = useRef<HTMLDivElement>(null);
    const [accordionSummaryTop, setAccordionSummaryTop] = useState<number>(0);

    const [packageSpecificLocalChannels, setPackageSpecificLocalChannels] = useState({});
    useEffect(() => {
      const modalElement = zipCodeModalRef.current;
      if (!modalElement) {
        return;
      }

      const observer = new ResizeObserver(() => {
        const height = modalElement.offsetHeight || 0;
        setAccordionSummaryTop(height - 4);
      });

      observer.observe(modalElement);

      return () => {
        observer.disconnect();
      };
    }, []);
    const initialAccordionState = data?.packages?.reduce((acc, pkg, index) => {
      acc[index] = true;
      // Set accordion state to false for MYFREE_DTV package on initial load
      if (pkg?.packageKey === "MYFREE_DTV") {
        acc[index] = false;
      }
      return acc;
    }, {});
    const [accordionExpanded, setAccordionExpanded] = useState<{[key: number]: boolean; }>(
      initialAccordionState
    );
    const handleAccordionToggle = useCallback((index: number) => {
      setAccordionExpanded(prevExpanded => ({
        ...prevExpanded,
        [index]: !prevExpanded[index]
      }));
    }, []);

    /** Local Rsn API Logic -- Start -- */
    const [myHomeTeamRsnChannels, setMyHomeTeamRsnChannels] = useState([]);

    // Filter local channels based on DMA eligible & genreEligible & queryParameter
    const handleAddLocalChannelsToPackages = (localRsnChannels, dma, nielsenDma) => {
      const basePackages = basePackagesRef.current || [];
      const nextPackages = basePackages.map((basePack: any) => {
        const genrePack = {
          ...basePack,
          channels: [...(basePack.channels || [])]
        };
        genrePack.channels = genrePack?.channels?.filter(channel => !(channel?.isLocal || channel?.isRsn));
        if (
          (!props?.queryParameters) ||
          (props?.queryParameters?.length > 0 && props?.queryParameters?.includes(genrePack?.contentFriendlyPackageKey))
        ) {
          let filteredLocalChannels = handleLocalRsnChannelFilter(localRsnChannels, props?.genreLocalChannelMapping, genrePack?.contentFriendlyPackageKey, dma, nielsenDma, props?.genreNationals?.genreNationalMapping, genrePack?.packageKey);
          filteredLocalChannels = filteredLocalChannels.map(channel => ({
            ...channel,
            channelNameLabel: channel?.network,
            channelLogoPath: channel?.logo,
            isLocal: channel?.TYPE === "LOCAL",
            isRsn: channel?.TYPE === "RSN"
          }));
          if (genrePack?.packageKey === "ATT_TV_MYHOMETEAM") {
            setMyHomeTeamRsnChannels(filteredLocalChannels);
            // Remove Duplicates MyHomeTeam National channels from filteredLocalChannels
            genrePack.channels = props?.myHomeTeamNationalChannels?.filter(channel => !filteredLocalChannels?.some(localChannel => localChannel?.network === channel?.channelNameLabel))
          } else {
            const masterChannels = [...(basePackages.find(pkg => pkg?.contentFriendlyPackageKey === genrePack?.contentFriendlyPackageKey)?.channels || [])];
            // Genre Local/National DMA Eligibility check - start
            let updatedNationalChannels = [];
            let updatedLocalChannels = [];
            try {
              const result = handleNationalChannelDMAFilter({
                filteredLocalChannels,
                nationalChannels: [...masterChannels || []],
                currentDma: dma || nielsenDma,
                nationalMapping: props?.genreNationals?.genreNationalMapping || [],
                packageKey: genrePack?.contentFriendlyPackageKey
              });
              updatedNationalChannels = result?.filteredNationalChannels || [];
              updatedLocalChannels = result?.updatedLocalChannels || [];
            } catch (err) {
              console.error("Error in [ChannelPackLineup] handleNationalChannelDMAFilter:", err);
              updatedNationalChannels = [...masterChannels || []];
              updatedLocalChannels = [...filteredLocalChannels || []];
            }
            // Genre Local/National DMA Eligibility check - end
            genrePack.channels = [...updatedNationalChannels, ...updatedLocalChannels];
          }
          const filteredLocals = genrePack?.packageKey === "ATT_TV_MYHOMETEAM" ? [] : filteredLocalChannels.filter(channel => channel?.isLocal && props?.showLocalChannelsPerGenre?.includes(channel?.channelNameLabel)).sort((a, b) => a?.channelNameLabel?.localeCompare(b?.channelNameLabel)).reduce((a, b) => ({
            ...a,
            [b?.channelNameLabel]: b
          }), {});
          setPackageSpecificLocalChannels((prevState => ({
            ...prevState,
            [genrePack?.packageKey]: filteredLocals
          })))
          genrePack?.channels?.sort((a, b) => a?.channelNameLabel?.localeCompare(b?.channelNameLabel));
        }
        return genrePack;
      });
      setData(prevData => ({
        ...prevData,
        packages: nextPackages
      }));
      setLoading(false);
    };

    const setLoadingLocalRsnChannels = React.useCallback(() => {
      setLoading(true);
    }, []);

    const addLocalRsnChnls = React.useCallback((channels: LocalRsnApiResponse | null, dma?: string, nielsenDma?: string) => {
      const { localChannels = [], rsnChannels = [] } = channels;
      console.log("Channel Pack Lineup fetched LocalRsn Chnls >>", localChannels, rsnChannels);
      handleAddLocalChannelsToPackages([...(localChannels || []), ...(rsnChannels || [])], dma, nielsenDma);
    }, []);

    const handleLocalRsnApiError = error => {
      setLoading(false);
      console.error("Channel Pack Lineup >> Local/Rsn API Error..", error);
    };
    /** Local Rsn API Logic -- End -- */

    // Generated by Copilot- Start - 2026-04-13 14:06:05
    const urlZipcode = (props.zipcode && /^\d{5}$/.test(props.zipcode)) ? props.zipcode : null;
    // Generated by Copilot- End - 2026-04-13 14:06:05

    const handleExpandCtaClick = useCallback((event: React.MouseEvent | React.KeyboardEvent, name: string | null, _expandCta: string) => {
      setOpenSubscription(prev => ({
        name,
        open: prev.name === name ? !prev.open : true
      }));
      event?.stopPropagation();
    }, []);

    const getModalContent = () => (
      <DtvBox
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          position: "relative"
        }}
      >
        {/* heading */}
        <DtvBox
        ref={zipCodeModalRef}
        sx={{
            height: "auto",
            position: "sticky",
            top: -4,
            zIndex: 5,
            background: "#fff"

        }}
        >
        <DtvGrid sx={{ ...styles.headingContainer }}>
          <DtvText content={data?.heading} sx={{ ...styles.heading }} variant="subhead1medium" />
       </DtvGrid>

        {/* Zip Code Module */}
        <DtvGrid
          sx={{
            ...styles.headingContainer
          }}
        >
          <ZipcodeModule
            ciConfig={props?.zipcodeModuleConfig}
            data-testid="zipcode-module"
            id="chnl-pck-lnp-zip-module"
            initData={urlZipcode ? { zipcode: urlZipcode } : null}
            isConditional={!!urlZipcode}
            mode="light"
            position="left"
            size="small"
            localRsnApiOptions={{
 satellite: false,
stream: true
}}
            onLocalRsnChnlsApiError={handleLocalRsnApiError}
            onLocalRsnChnlsConfirmed={addLocalRsnChnls}
            onSearchButtonClick={setLoadingLocalRsnChannels}
          />
        </DtvGrid>
        </DtvBox>

        {/* body */}
        <DtvGrid
          sx={{
            // Sticky-summary offset as a CSS custom property (read by every panel) so the
            // ResizeObserver height updates don't invalidate the memoized panels via a prop.
            [SUMMARY_TOP_VAR]: `${accordionSummaryTop}px`
          }}
        >
          {data?.packages &&
            data?.packages?.map((pack, index) => {
              const showAccordion = (((props?.queryParameters?.includes?.("myhometeam") || !props?.queryParameters) && pack?.packageKey === "ATT_TV_MYHOMETEAM") || (pack?.channels?.length > 0));
              if (!showAccordion) {
 return null;
} // Skip rendering if no channels or not applicable
              let nationalDisclaimerFlag = true
              if(Object.keys(packageSpecificLocalChannels).length > 0 && Object.keys(packageSpecificLocalChannels?.[pack?.packageKey]).length > 0) {
                const localChannelsForPack = Object.values(packageSpecificLocalChannels?.[pack?.packageKey]);
                localChannelsForPack?.forEach((localChannel: any) => {
                  if (!localChannel?.isNational) {
                    nationalDisclaimerFlag = false;
                  }
                })
              } else {
                nationalDisclaimerFlag = false;
              }
              // Panels not containing the open subscription all receive the same null, so
              // their memo holds; only the panel(s) owning the name re-render on a sub tap.
              let openSubscriptionName: string | null = null;
              try {
                if (openSubscription.open && openSubscription.name != null &&
                    pack?.subscriptions?.some(sub => sub?.name === openSubscription.name)) {
                  openSubscriptionName = openSubscription.name;
                }
              } catch {
                // covers ONLY this subscriptions read: a throwing/malformed subscriptions
                // field stays a panel-local failure instead of blanking the whole modal.
                // (channels/locals reads in this map are still owned by the parent catch.)
                openSubscriptionName = null;
              }
              return (
                <ChannelPackPanel
                  expanded={accordionExpanded[index]}
                  index={index}
                  key={index}
                  loading={loading}
                  localChannelsInPackage={props?.localChannelsInPackage}
                  localsSectionLocationTop={props?.localsSectionLocationTop}
                  localsSectionTooltip={props?.localsSectionTooltip}
                  genreNationalMapping={props?.genreNationals?.genreNationalMapping}
                  myHomeTeamRsnChannels={myHomeTeamRsnChannels}
                  nationalDisclaimerFlag={nationalDisclaimerFlag}
                  nationalsDisclaimerText={props?.nationalsDisclaimerText}
                  noLocalsInAreaText={props?.noLocalsInAreaText}
                  noLocalsInPackText={props?.noLocalsInPackText}
                  onExpandCtaClick={handleExpandCtaClick}
                  onToggle={handleAccordionToggle}
                  openSubscriptionName={openSubscriptionName}
                  pack={pack}
                  packageSpecificLocalChannels={packageSpecificLocalChannels}
                  rsnTeamsTableData={props?.rsnTeamsTableData}
                  showLocalChannelsPerGenre={props?.showLocalChannelsPerGenre}
                  subscriptionsTitle={data?.subscriptionsTitle}
                />
              );
            })}
        </DtvGrid>

      </DtvBox>
    );

    return (
      <DtvBox
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {getModalContent()}
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="DTV ChannelPackLineup component" data-testid="error-display" error={error} />;
  }
}
